import { Form, Input, theme } from 'antd';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import Modal from '../../../../shared/components/modal/modal';
import { handleServerFormErrors } from '../../../../shared/utils/error-handling/error-handling';
import { useAppSelector } from '../../../../store/hooks';
import LazyModal, { LazyModalComponentProps } from '../../../ui/components/lazy-modal';
import { closeModal, setAlert } from '../../../ui/store/ui-slice';
import { useEditUserPasswordMutation } from '../../api/user-api';
import { currentUserAlertId, currentUserPasswordModalId } from '../../constants/constants';
import { UserPasswordModel } from '../../models/user-password-model';
import { selectActiveCurrentUserId } from '../../store/current-user-slice';
import styles from './current-user-password-modal.module.css';

interface PasswordChangeFormModel extends UserPasswordModel {
  plainPasswordRepeat: string;
}

interface Props {
  isForcedChangePassword?: boolean;
}

const CurrentUserPassword = ({
  isOpen,
  afterClose,
  isForcedChangePassword,
}: LazyModalComponentProps & Props): JSX.Element => {
  const { t } = useTranslation('current-user');
  const dispatch = useDispatch();
  const activeCurrentUserId = useAppSelector(selectActiveCurrentUserId);
  const {
    token: { colorTextTertiary },
  } = theme.useToken();

  const [form] = Form.useForm<PasswordChangeFormModel>();
  const [editCurrentUserPassword, { isLoading: isEditCurrentUserPasswordLoading }] = useEditUserPasswordMutation();

  const closePasswordEditModal = () =>
    !isEditCurrentUserPasswordLoading && dispatch(closeModal(currentUserPasswordModalId));

  const submitForm = (formData: UserPasswordModel) => {
    if (!activeCurrentUserId) {
      return;
    }

    editCurrentUserPassword({
      ...formData,
      id: activeCurrentUserId,
    })
      .unwrap()
      .then(() => {
        closePasswordEditModal();
        dispatch(
          setAlert({
            id: currentUserAlertId,
            type: 'success',
            title: t('editPasswordModal.message.passwordEditSuccess'),
          }),
        );
      })
      .catch(error => {
        handleServerFormErrors(form, error.data?.errors);
      });
  };

  useEffect(() => {
    form.resetFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal
      title={t('action.changePassword')}
      isOpen={isOpen}
      isLoading={isEditCurrentUserPasswordLoading}
      afterClose={afterClose}
      onOk={() => form.submit()}
      onCancel={closePasswordEditModal}
      formId={currentUserPasswordModalId}
      isClosable={!isForcedChangePassword}
    >
      <Form form={form} onFinish={submitForm} id={currentUserPasswordModalId}>
        <Form.Item
          name="oldPassword"
          rules={[{ required: true, message: t('editPasswordModal.message.oldPasswordRequired') }]}
        >
          <Input.Password placeholder={t('editPasswordModal.currentPassword')} />
        </Form.Item>
        <Form.Item
          name="plainPassword"
          rules={[{ required: true, message: t('editPasswordModal.message.newPasswordRequired') }]}
          className={styles.newPasswordFormItem}
        >
          <Input.Password placeholder={t('editPasswordModal.newPassword')} />
        </Form.Item>
        <ul style={{ color: colorTextTertiary }}>
          <li>{t('editPasswordModal.hint.passwordLength')}</li>
          <li>{t('editPasswordModal.hint.passwordContainsNumber')}</li>
          <li>{t('editPasswordModal.hint.passwordContainsCharacter')}</li>
        </ul>
        <Form.Item
          name="plainPasswordRepeat"
          rules={[
            { required: true, message: t('editPasswordModal.message.repeatNewPasswordRequired') },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('plainPassword') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(t('editPasswordModal.message.passwordMismatch')));
              },
            }),
          ]}
          validateTrigger="onBlur"
        >
          <Input.Password placeholder={t('editPasswordModal.repeatNewPassword')} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const CurrentUserPasswordModal = (): JSX.Element => {
  return <LazyModal modalId={currentUserPasswordModalId} Component={CurrentUserPassword} />;
};

export default CurrentUserPasswordModal;
